import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Store } from 'types/store';
import useLoyaltyRewards from 'hooks/journeys/loyaltyRewards/useLoyaltyRewards';
import useErrorHandler from 'hooks/journeys/loyaltyRewards/useErrorHandler';
import { ClaimRewardError, RLRLocationState } from 'types/journeys/loyaltyRewards';
import { RLRProductCodesEnum, RLRProductType, RLRVehicleInfoEnum } from 'constants/journeys/loyaltyRewardsEnums';
import useViewport from 'utils/useViewportHelper';
import Spinner from 'components/Spinner';
import imageDesktop from 'assets/images/loyaltyRewards/popup/desktop.jpg';
import imageMobile from 'assets/images/loyaltyRewards/popup/mobile.jpg';
import trackEvent from 'services/AnalyticsService';
import TermsAndConditions from './TermsAndConditions';
import ErrorMessage from './ErrorMessage';
import ProductCard from './ProductCard';
import styles from './Options.module.scss';

const Options: React.FC = () => {
  const location = useLocation();

  const { rlrJourneyDetails } = (location.state as RLRLocationState) || {};
  const sourceOfClaim = rlrJourneyDetails ? 1 : 0;

  const { pageModel, journeyError, vehicleDisplayModality } = useSelector((state: Store) => state.journeys.loyaltyRewards);
  const { selectedReward, chosenVehicle } = useSelector((state: Store) => state.journeys.loyaltyRewards.data);

  const [loading, setLoading] = useState<boolean>(true);
  const [claimingReward, setClaimingReward] = useState<boolean>(false);
  const [claimRewardError, setClaimRewardError] = useState<ClaimRewardError>(null);
  const [ipiDocumentUrl, setIpiDocumentUrl] = useState<string | null>(null);
  const [products, setProducts] = useState<RLRProductType[]>([]);
  const [isExpiryJourney, setIsExpiryJourney] = useState<boolean>(false);

  const currentMobileState = useViewport().isMobile;

  const selectedBatteryOrTyreReplace = selectedReward && (selectedReward.productCode === RLRProductCodesEnum.DRV_BATTERY || selectedReward.productCode === RLRProductCodesEnum.DRV_TYRE);
  const vehicleSearchOff = RLRVehicleInfoEnum.NoVehicleCapture === vehicleDisplayModality;
  const disableCTAWithNoVehicleSelected = selectedBatteryOrTyreReplace && !chosenVehicle && !vehicleSearchOff;
  const disableCTA = !selectedReward?.productCode || claimingReward || disableCTAWithNoVehicleSelected;

  const isEligible = pageModel?.policyRewardEligibility === 'PolicyEligibleForRewards';
  const duration = pageModel?.coverDuration !== 'Other' ? `the next ${pageModel?.coverDuration} months` : 'the next policy term';

  const { fetchRLROffer, claimReward } = useLoyaltyRewards(
    rlrJourneyDetails,
    setLoading,
    setClaimingReward,
    setClaimRewardError,
    setIpiDocumentUrl,
    setProducts,
    setIsExpiryJourney,
  );

  useErrorHandler({
    loading,
    isEligible,
    claimRewardError,
  });

  useEffect(() => {
    fetchRLROffer();
  }, []);

  useEffect(() => {
    if (pageModel && !loading && !journeyError && products?.length > 0) {
      if (isExpiryJourney) {
        trackEvent('MyRAC_RLR_Expiry_LandingPage');
      } else {
        trackEvent('MyRAC_RLR_LandingPage');
      }
    }
  }, [pageModel, loading, journeyError, products]);

  useEffect(() => {
    if (!vehicleSearchOff) {
      trackEvent('MyRAC_RLR_Vehicle_Capture_Displayed');
    }
  }, [vehicleSearchOff]);

  if (loading) {
    return <div className={styles.loadingScreen}><Spinner bootSpinner /></div>;
  }

  if (pageModel && !loading && !journeyError && products?.length > 0) {
    return (
      <>
        <div className={styles.headerImage}>
          <img src={currentMobileState ? imageMobile : imageDesktop} alt="Free upgrade" />
        </div>
        <p className={styles.headerParagraph}>
          {isExpiryJourney ? (
            <>Choose an upgrade and we’ll add it to next year’s cover - free!<br />Then click through to renew.</>
          ) : (
            <>Choose your upgrade below and we’ll add it to your cover for {duration}. Just a little thank you for staying with us.</>
          )}
        </p>
        <div className={styles.termsAndConditionsButtonWrap}>
          <a href="#termsAndConditions" className={styles.termsAndConditionsButton}>Terms and conditions</a>
        </div>
        <div className={styles.rewards}>
          {products.map((product) => (
            <ProductCard
              key={product.productCode}
              product={product}
              ipiDocumentUrl={ipiDocumentUrl}
            />
        ))}
        </div>
        <TermsAndConditions isExpiryJourney={isExpiryJourney} />
        <div className={`${styles.floatingConfirmButton} ${selectedReward?.productCode ? 'position-sticky' : ''}`}>
          <span>By choosing your upgrade, you’re confirming you’ve read the T&Cs, and the upgrade you’ve selected is right for you.</span>
          <button onClick={() => claimReward(isExpiryJourney, sourceOfClaim)} disabled={!!disableCTA} className={styles.mainButton} type="button">
            {!claimingReward && (isExpiryJourney ? 'Continue to renewal' : 'Confirm')}
            {claimingReward && (
              <>
                <div className="spinner-border spinner-border-sm p-2 mx-2" role="status" />
                Loading...
              </>
            )}
          </button>
        </div>
      </>
    )
  }

  return (
    <ErrorMessage />
  );
};

export default Options;
